// PainelProfissional.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Star,
    User,
    Briefcase,
    Clock,
    CheckCircle,
    Building2,
    Mail,
    Phone,
    MapPin,
    Eye,
    MessageCircle,
    FileCheck,
    ArrowLeft,
    Crown,
    Shield,
    BadgeCheck,
    AlertCircle,
    Loader2,
    DollarSign,
    ChevronRight,
} from "lucide-react";

import "./PainelProfissional.scss";

// Services
import { getMe } from "../../services/authService";
import { getWallet } from "../../services/walletService";
import { getMyApplications } from "../../services/applicationService";
import {
    getMyRatings,
    getEvaluationSummary,
} from "../../services/ratingService";
import { loadJobs } from "../../services/jobService";

const PainelProfissional = () => {
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [wallet, setWallet] = useState(null);
    const [applications, setApplications] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [ratings, setRatings] = useState([]);
    const [ratingSummary, setRatingSummary] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [stats, setStats] = useState({
        totalJobs: 0,
        completedJobs: 0,
        inProgressJobs: 0,
        averageRating: 0,
        totalRatings: 0,
        totalEarned: 0,
    });

    // =========================================================
    // CARREGAMENTO
    // =========================================================

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userData = await getMe();

            if (!userData) {
                throw new Error("Usuário não encontrado.");
            }

            setUser(userData);

            if (userData.userType === "company") {
                await loadCompanyData(userData.id);
            } else {
                await loadFreelancerData();
            }

            await Promise.all([
                loadWalletData(),
                loadRatingsData(userData.id),
            ]);
        } catch (err) {
            console.error("Erro ao carregar dados do painel:", err);

            setError(
                "Não foi possível carregar os dados do painel. Tente novamente."
            );
        } finally {
            setLoading(false);
        }
    };

    // =========================================================
    // EMPRESA
    // =========================================================

    const loadCompanyData = async (companyId) => {
        try {
            const jobsData = await loadJobs(companyId);
            const safeJobs = Array.isArray(jobsData) ? jobsData : [];

            setJobs(safeJobs);

            const completedJobs = safeJobs.filter((job) =>
                ["completed", "finalizada"].includes(
                    String(job.status || job.publishStatus).toLowerCase()
                )
            );

            const inProgressJobs = safeJobs.filter((job) =>
                ["in_progress", "andamento", "active"].includes(
                    String(job.status || job.publishStatus).toLowerCase()
                )
            );

            setStats((prev) => ({
                ...prev,
                totalJobs: safeJobs.length,
                completedJobs: completedJobs.length,
                inProgressJobs: inProgressJobs.length,
            }));
        } catch (err) {
            console.error("Erro ao carregar dados da empresa:", err);
        }
    };

    // =========================================================
    // FREELANCER
    // =========================================================

    const loadFreelancerData = async () => {
        try {
            const appsData = await getMyApplications();
            const safeApplications = Array.isArray(appsData)
                ? appsData
                : [];

            setApplications(safeApplications);

            const completed = safeApplications.filter((application) =>
                ["completed", "finalizada"].includes(
                    String(application.status).toLowerCase()
                )
            );

            const inProgress = safeApplications.filter((application) =>
                ["in_progress", "andamento", "active"].includes(
                    String(application.status).toLowerCase()
                )
            );

            setStats((prev) => ({
                ...prev,
                totalJobs: safeApplications.length,
                completedJobs: completed.length,
                inProgressJobs: inProgress.length,
            }));
        } catch (err) {
            console.error("Erro ao carregar dados do freelancer:", err);
        }
    };

    // =========================================================
    // CARTEIRA
    // =========================================================

    const loadWalletData = async () => {
        try {
            const walletData = await getWallet();

            setWallet(walletData);

            const balance = Number(walletData?.balance || 0);
            const reserved = Number(walletData?.reserved || 0);

            const availableBalance = Math.max(balance - reserved, 0);

            setStats((prev) => ({
                ...prev,
                totalEarned: availableBalance,
            }));
        } catch (err) {
            console.error("Erro ao carregar carteira:", err);
        }
    };

    // =========================================================
    // AVALIAÇÕES
    // =========================================================

    const loadRatingsData = async (userId) => {
        try {
            const ratingsData = await getMyRatings();

            const mappedRatings = Array.isArray(ratingsData)
                ? ratingsData.map((rating) => ({
                      id: rating.id,
                      rating: Number(
                          rating.rating || rating.score || 0
                      ),
                      comment:
                          rating.comment ||
                          rating.review ||
                          "",
                      createdAt:
                          rating.createdAt ||
                          rating.createdDate,
                      evaluatorName:
                          rating.evaluatorName ||
                          rating.creatorName ||
                          rating.evaluator?.name ||
                          rating.creator?.name ||
                          rating.company?.name ||
                          rating.freelancer?.name ||
                          "Usuário",
                      evaluatorAvatar:
                          rating.evaluatorAvatar ||
                          rating.creatorAvatar ||
                          rating.evaluator?.avatarUrl ||
                          rating.creator?.avatarUrl ||
                          rating.company?.avatarUrl ||
                          rating.freelancer?.avatarUrl ||
                          null,
                  }))
                : [];

            setRatings(mappedRatings);

            const summary = await getEvaluationSummary(userId);

            setRatingSummary(summary);

            if (mappedRatings.length > 0) {
                const average =
                    mappedRatings.reduce(
                        (total, rating) => total + rating.rating,
                        0
                    ) / mappedRatings.length;

                setStats((prev) => ({
                    ...prev,
                    averageRating: average,
                    totalRatings: mappedRatings.length,
                }));
            }

            if (summary) {
                setStats((prev) => ({
                    ...prev,
                    averageRating:
                        Number(summary.averageRating) ||
                        prev.averageRating,
                    totalRatings:
                        Number(summary.totalEvaluations) ||
                        prev.totalRatings,
                }));
            }
        } catch (err) {
            console.error("Erro ao carregar avaliações:", err);
        }
    };

    // =========================================================
    // DADOS DERIVADOS
    // =========================================================

    const isCompany = user?.userType === "company";
    const isFreelancer = user?.userType === "freelancer";

    const currentPlan = String(user?.currentPlan || "").toLowerCase();

    const isGold =
        Boolean(user?.isGold) ||
        currentPlan.includes("gold");

    const isPremium = currentPlan.includes("premium");
    const isPlus = currentPlan.includes("plus");

    const address = user?.address || null;

    const formattedAddress = useMemo(() => {
        if (!address) return null;

        const parts = [
            address.logradouro,
            address.numero,
            address.bairro,
            address.cidade,
            address.estado,
        ].filter(Boolean);

        return parts.length > 0 ? parts.join(", ") : null;
    }, [address]);

    // =========================================================
    // HELPERS
    // =========================================================

    const formatStatus = (status) => {
        const statusMap = {
            open: "Aberta",
            in_progress: "Em andamento",
            completed: "Finalizada",
            finalizada: "Finalizada",
            andamento: "Em andamento",
            pending: "Pendente",
            approved: "Aprovado",
            active: "Ativa",
            published: "Publicada",
            draft: "Rascunho",
            rejected: "Rejeitada",
            cancelled: "Cancelada",
        };

        const normalizedStatus = String(status || "").toLowerCase();

        return (
            statusMap[normalizedStatus] ||
            status ||
            "Desconhecido"
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Data desconhecida";

        try {
            return new Date(dateString).toLocaleDateString(
                "pt-BR",
                {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                }
            );
        } catch {
            return "Data inválida";
        }
    };

    const formatCurrency = (value) => {
        return Number(value || 0).toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
        });
    };

    const renderStars = (rating = 0) => {
        const roundedRating = Math.round(Number(rating) || 0);

        return Array.from({ length: 5 }, (_, index) => {
            const active = index < roundedRating;

            return (
                <Star
                    key={index}
                    size={15}
                    className="painel-profissional__star"
                    fill={active ? "currentColor" : "none"}
                />
            );
        });
    };

    const getAvatarUrl = (name, avatar) => {
        if (avatar) return avatar;

        return `https://ui-avatars.com/api/?name=${encodeURIComponent(
            name || "Usuario"
        )}&background=1EC88A&color=fff&size=128`;
    };

    // =========================================================
    // LOADING
    // =========================================================

    if (loading) {
        return (
            <div className="painel-profissional painel-profissional--state">
                <div className="painel-profissional__loading">
                    <div className="painel-profissional__loading-icon">
                        <Loader2 size={32} />
                    </div>

                    <h3>Carregando seu painel</h3>

                    <p>
                        Estamos preparando suas informações...
                    </p>
                </div>
            </div>
        );
    }

    // =========================================================
    // ERROR
    // =========================================================

    if (error) {
        return (
            <div className="painel-profissional painel-profissional--state">
                <div className="painel-profissional__error">
                    <div className="painel-profissional__error-icon">
                        <AlertCircle size={32} />
                    </div>

                    <h3>Ops! Algo deu errado</h3>

                    <p>{error}</p>

                    <button
                        type="button"
                        onClick={loadData}
                    >
                        Tentar novamente
                    </button>
                </div>
            </div>
        );
    }

    // =========================================================
    // RENDER
    // =========================================================

    return (
        <div className="painel-profissional">
            <div className="painel-profissional__container">

                {/* =====================================================
                    HEADER
                ====================================================== */}

                <header className="painel-profissional__header">
                    <button
                        type="button"
                        className="painel-profissional__back-btn"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowLeft size={18} />
                        <span>Voltar</span>
                    </button>

                    <div className="painel-profissional__heading">
                        <span className="painel-profissional__eyebrow">
                            Visão geral
                        </span>

                        <h1 className="painel-profissional__title">
                            {isCompany
                                ? "Painel Corporativo"
                                : "Meu Painel"}
                        </h1>
                    </div>
                </header>

                {/* =====================================================
                    PERFIL
                ====================================================== */}

                <section className="painel-profissional__profile-card">

                    <div className="painel-profissional__profile-main">

                        <div className="painel-profissional__profile-avatar">
                            <img
                                src={getAvatarUrl(
                                    user?.name,
                                    user?.avatarUrl
                                )}
                                alt={`Foto de ${user?.name || "Usuário"}`}
                            />

                            {user?.documentStatus === "approved" && (
                                <span
                                    className="painel-profissional__verified-badge"
                                    title="Perfil verificado"
                                >
                                    <BadgeCheck
                                        size={20}
                                        fill="#1EC88A"
                                        stroke="white"
                                    />
                                </span>
                            )}
                        </div>

                        <div className="painel-profissional__profile-info">

                            <div className="painel-profissional__profile-name-row">
                                <h2>
                                    {user?.name || "Usuário"}
                                </h2>

                                {isGold && (
                                    <span className="painel-profissional__mini-gold">
                                        <Crown size={13} />
                                        Gold
                                    </span>
                                )}
                            </div>

                            <div className="painel-profissional__profile-role">
                                {isCompany ? (
                                    <>
                                        <Building2 size={15} />
                                        Empresa
                                    </>
                                ) : (
                                    <>
                                        <User size={15} />
                                        Profissional
                                    </>
                                )}
                            </div>

                            <div className="painel-profissional__profile-rating">
                                <div className="painel-profissional__stars">
                                    {renderStars(stats.averageRating)}
                                </div>

                                <strong>
                                    {Number(
                                        stats.averageRating || 0
                                    ).toFixed(1)}
                                </strong>

                                <span>
                                    {stats.totalRatings}{" "}
                                    {stats.totalRatings === 1
                                        ? "avaliação"
                                        : "avaliações"}
                                </span>
                            </div>

                            <div className="painel-profissional__contact-list">

                                {user?.email && (
                                    <span>
                                        <Mail size={14} />
                                        {user.email}
                                    </span>
                                )}

                                {user?.phone && (
                                    <span>
                                        <Phone size={14} />
                                        {user.phone}
                                    </span>
                                )}

                                {formattedAddress && (
                                    <span>
                                        <MapPin size={14} />
                                        {formattedAddress}
                                    </span>
                                )}

                            </div>
                        </div>
                    </div>

                    {/* <button
                        type="button"
                        className="painel-profissional__profile-action"
                        onClick={() => navigate("/perfil")}
                    >
                        Ver perfil
                        <ChevronRight size={16} />
                    </button> */}

                </section>

                {/* =====================================================
                    ESTATÍSTICAS
                ====================================================== */}

                <section className="painel-profissional__stats-grid">

                    <div className="painel-profissional__stat-card">
                        <div className="painel-profissional__stat-icon painel-profissional__stat-icon--green">
                            <Briefcase size={21} />
                        </div>

                        <div>
                            <span className="painel-profissional__stat-value">
                                {stats.totalJobs}
                            </span>

                            <span className="painel-profissional__stat-label">
                                {isCompany
                                    ? "Vagas criadas"
                                    : "Candidaturas"}
                            </span>
                        </div>
                    </div>

                    <div className="painel-profissional__stat-card">
                        <div className="painel-profissional__stat-icon painel-profissional__stat-icon--orange">
                            <Clock size={21} />
                        </div>

                        <div>
                            <span className="painel-profissional__stat-value">
                                {stats.inProgressJobs}
                            </span>

                            <span className="painel-profissional__stat-label">
                                Em andamento
                            </span>
                        </div>
                    </div>

                    <div className="painel-profissional__stat-card">
                        <div className="painel-profissional__stat-icon painel-profissional__stat-icon--blue">
                            <CheckCircle size={21} />
                        </div>

                        <div>
                            <span className="painel-profissional__stat-value">
                                {stats.completedJobs}
                            </span>

                            <span className="painel-profissional__stat-label">
                                Finalizados
                            </span>
                        </div>
                    </div>

                    <div className="painel-profissional__stat-card">
                        <div className="painel-profissional__stat-icon painel-profissional__stat-icon--purple">
                            <DollarSign size={21} />
                        </div>

                        <div>
                            <span className="painel-profissional__stat-value painel-profissional__stat-value--currency">
                                {formatCurrency(
                                    stats.totalEarned
                                )}
                            </span>

                            <span className="painel-profissional__stat-label">
                                {isCompany
                                    ? "Disponível"
                                    : "Ganhos"}
                            </span>
                        </div>
                    </div>

                </section>

                {/* =====================================================
                    AVALIAÇÕES
                ====================================================== */}

                <section className="painel-profissional__section">

                    <div className="painel-profissional__section-header">

                        <div>
                            <span className="painel-profissional__section-eyebrow">
                                Reputação
                            </span>

                            <h3>
                                <Star size={18} />
                                Avaliações recentes
                            </h3>
                        </div>

                        {ratings.length > 3 && (
                            <button
                                type="button"
                                className="painel-profissional__see-all"
                            >
                                Ver todas
                                <Eye size={15} />
                            </button>
                        )}

                    </div>

                    {ratings.length === 0 ? (
                        <div className="painel-profissional__empty-state">
                            <div className="painel-profissional__empty-icon">
                                <MessageCircle size={25} />
                            </div>

                            <p>Nenhuma avaliação ainda</p>

                            <span>
                                As avaliações aparecerão aqui após
                                trabalhos concluídos.
                            </span>
                        </div>
                    ) : (
                        <div className="painel-profissional__ratings-list">

                            {ratings.slice(0, 3).map((rating) => {
                                const evaluatorName =
                                    rating.evaluatorName ||
                                    "Usuário";

                                return (
                                    <article
                                        key={rating.id}
                                        className="painel-profissional__rating-item"
                                    >
                                        <div className="painel-profissional__rating-user">

                                            <img
                                                src={getAvatarUrl(
                                                    evaluatorName,
                                                    rating.evaluatorAvatar
                                                )}
                                                alt={evaluatorName}
                                                onError={(event) => {
                                                    event.currentTarget.src =
                                                        getAvatarUrl(
                                                            evaluatorName
                                                        );
                                                }}
                                            />

                                            <div className="painel-profissional__rating-user-info">

                                                <strong>
                                                    {evaluatorName}
                                                </strong>

                                                <div className="painel-profissional__rating-stars">
                                                    {renderStars(
                                                        rating.rating
                                                    )}
                                                </div>

                                            </div>
                                        </div>

                                        <time className="painel-profissional__rating-date">
                                            {formatDate(
                                                rating.createdAt
                                            )}
                                        </time>

                                        {rating.comment && (
                                            <p className="painel-profissional__rating-comment">
                                                “{rating.comment}”
                                            </p>
                                        )}
                                    </article>
                                );
                            })}

                        </div>
                    )}
                </section>

                {/* =====================================================
                    ATIVIDADES
                ====================================================== */}

                <section className="painel-profissional__section">

                    <div className="painel-profissional__section-header">

                        <div>
                            <span className="painel-profissional__section-eyebrow">
                                Histórico
                            </span>

                            <h3>
                                <Clock size={18} />
                                Últimas atividades
                            </h3>
                        </div>

                    </div>

                    <div className="painel-profissional__activities-list">

                        {isCompany ? (
                            jobs.length > 0 ? (
                                jobs.slice(0, 5).map((job, index) => (
                                    <div
                                        key={
                                            job.id ||
                                            `job-${index}`
                                        }
                                        className="painel-profissional__activity-item"
                                    >
                                        <div className="painel-profissional__activity-icon painel-profissional__activity-icon--green">
                                            <Briefcase size={17} />
                                        </div>

                                        <div className="painel-profissional__activity-content">
                                            <strong>
                                                {job.title ||
                                                    job.name ||
                                                    "Vaga sem título"}
                                            </strong>

                                            <div>
                                                <span className="painel-profissional__status">
                                                    {formatStatus(
                                                        job.status ||
                                                            job.publishStatus
                                                    )}
                                                </span>

                                                <span className="painel-profissional__activity-date">
                                                    {formatDate(
                                                        job.updatedAt ||
                                                            job.createdAt
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="painel-profissional__empty-state">
                                    <div className="painel-profissional__empty-icon">
                                        <Briefcase size={25} />
                                    </div>

                                    <p>
                                        Nenhuma atividade recente
                                    </p>

                                    <span>
                                        Comece criando uma nova vaga.
                                    </span>
                                </div>
                            )
                        ) : applications.length > 0 ? (
                            applications.slice(0, 5).map(
                                (application, index) => (
                                    <div
                                        key={
                                            application.id ||
                                            `application-${index}`
                                        }
                                        className="painel-profissional__activity-item"
                                    >
                                        <div className="painel-profissional__activity-icon painel-profissional__activity-icon--blue">
                                            <FileCheck size={17} />
                                        </div>

                                        <div className="painel-profissional__activity-content">
                                            <strong>
                                                {application.jobTitle ||
                                                    application.title ||
                                                    "Vaga"}
                                            </strong>

                                            <div>
                                                <span className="painel-profissional__status">
                                                    {formatStatus(
                                                        application.status
                                                    )}
                                                </span>

                                                <span className="painel-profissional__activity-date">
                                                    {formatDate(
                                                        application.updatedAt ||
                                                            application.createdAt
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )
                            )
                        ) : (
                            <div className="painel-profissional__empty-state">
                                <div className="painel-profissional__empty-icon">
                                    <Briefcase size={25} />
                                </div>

                                <p>
                                    Nenhuma atividade recente
                                </p>

                                <span>
                                    Candidate-se a vagas para começar.
                                </span>
                            </div>
                        )}

                    </div>
                </section>

            </div>
        </div>
    );
};

export default PainelProfissional;